import { rngUnit } from "../../utils/rng";
import type { OrbitForgeSave, SupplyCrateCosmeticReward, SupplyCrateId, SupplyCrateResult } from "../save/types";
import {
  crateCosmetics,
  getPaidSupplyCrateDefinition,
  type CrateCosmeticDefinition,
  type PaidSupplyCrateDefinition
} from "./catalog";

interface RollContext {
  state: number;
}

interface RollInput {
  readonly save: OrbitForgeSave;
  readonly crateId: SupplyCrateId;
  readonly resultId: number;
  readonly openedAtUnixSeconds: number;
  readonly pricePaid: number;
  readonly baseValue: number;
}

interface RollOutput {
  readonly result: SupplyCrateResult;
  readonly nextRngState: number;
}

export function rollSupplyCrateResult(input: RollInput): RollOutput {
  const context: RollContext = { state: input.save.rngState };
  const result =
    input.crateId === "free"
      ? rollFreeSupplyDrop(input, context)
      : rollPaidSupplyCrate(input, getPaidSupplyCrateDefinition(input.crateId), context);

  return {
    result,
    nextRngState: context.state
  };
}

function rollFreeSupplyDrop(input: RollInput, context: RollContext): SupplyCrateResult {
  const lucky = rollChance(context, 0.1);
  const cashMultiplier = rollRange(context, lucky ? [0.95, 1.35] : [0.34, 0.62]);
  const ticketRolled = rollChance(context, 0.12);
  const ticketFallbackCash = ticketRolled && ticketStorageFull(input.save) ? Math.ceil(input.baseValue * 0.05) : 0;
  const coreBurst = lucky || rollChance(context, 0.28);
  const coreBurstCount = coreBurst ? rollInteger(context, 1, 2) : 0;
  const overchargedCoreMultiplier = rollOverchargedCoreMultiplier(context, "free", false);
  const cosmetic = rollCosmeticReward(context, input.save, "free", input.baseValue, false);

  return createResult(input, {
    cash: Math.ceil(input.baseValue * cashMultiplier) + ticketFallbackCash + (cosmetic?.cashBonus ?? 0),
    jackpot: false,
    lucky,
    ticketSeconds: ticketRolled && ticketFallbackCash === 0 ? 60 : null,
    ticketFallbackCash,
    coreBurstCount: Math.max(coreBurstCount, overchargedCoreMultiplier ? 1 : 0),
    overchargedCoreMultiplier,
    cosmetic
  });
}

function rollPaidSupplyCrate(input: RollInput, definition: PaidSupplyCrateDefinition, context: RollContext): SupplyCrateResult {
  const jackpot = rollChance(context, definition.jackpotChance);
  const cashMultiplier = rollRange(context, jackpot ? definition.jackpotRange : definition.cashRange);
  const ticketRolled = rollChance(context, definition.ticketChance);
  const ticketFallbackCash = ticketRolled && ticketStorageFull(input.save) ? Math.ceil(input.baseValue * 0.05) : 0;
  const coreBurst = rollChance(context, definition.coreChance);
  const coreBurstCount = coreBurst ? rollInteger(context, definition.coreCountRange[0], definition.coreCountRange[1]) : 0;
  const overchargedCoreMultiplier = rollOverchargedCoreMultiplier(context, definition.id, jackpot);
  const cosmetic = rollCosmeticReward(context, input.save, definition.id, input.baseValue, jackpot);

  return createResult(input, {
    cash: Math.ceil(input.baseValue * cashMultiplier) + ticketFallbackCash + (cosmetic?.cashBonus ?? 0),
    jackpot,
    lucky: false,
    ticketSeconds: ticketRolled && ticketFallbackCash === 0 ? definition.ticketSeconds : null,
    ticketFallbackCash,
    coreBurstCount: Math.max(coreBurstCount, overchargedCoreMultiplier ? 1 : 0),
    overchargedCoreMultiplier,
    cosmetic
  });
}

function createResult(
  input: RollInput,
  reward: Omit<SupplyCrateResult, "resultId" | "crateId" | "openedAtUnixSeconds" | "presentedAtUnixSeconds" | "pricePaid" | "baseValue">
): SupplyCrateResult {
  return {
    resultId: input.resultId,
    crateId: input.crateId,
    openedAtUnixSeconds: input.openedAtUnixSeconds,
    presentedAtUnixSeconds: null,
    pricePaid: input.pricePaid,
    baseValue: input.baseValue,
    ...reward
  };
}

function rollOverchargedCoreMultiplier(context: RollContext, crateId: SupplyCrateId, jackpot: boolean): number | null {
  const baseChances: Record<SupplyCrateId, number> = {
    free: 0.018,
    mini: 0.026,
    neon: 0.045,
    jackpot: 0.075
  };
  const chance = jackpot ? Math.min(0.13, baseChances[crateId] * 1.6) : baseChances[crateId];
  if (!rollChance(context, chance)) {
    return null;
  }

  if (rollChance(context, overchargedMultiplierChance(crateId, jackpot, "x100"))) {
    return 100;
  }

  if (rollChance(context, overchargedMultiplierChance(crateId, jackpot, "x20"))) {
    return 20;
  }

  return 10;
}

function overchargedMultiplierChance(crateId: SupplyCrateId, jackpot: boolean, tier: "x100" | "x20"): number {
  const chances: Record<typeof tier, Record<SupplyCrateId, number>> = {
    x100: { free: 0.006, mini: 0.01, neon: 0.016, jackpot: 0.03 },
    x20: { free: 0.04, mini: 0.06, neon: 0.12, jackpot: 0.22 }
  };
  const multiplier = tier === "x100" ? 1.45 : 1.35;
  const cap = tier === "x100" ? 0.055 : 0.34;
  const base = chances[tier][crateId];
  return jackpot ? Math.min(cap, base * multiplier) : base;
}

function rollCosmeticReward(
  context: RollContext,
  save: OrbitForgeSave,
  crateId: SupplyCrateId,
  baseValue: number,
  jackpot: boolean
): SupplyCrateCosmeticReward | null {
  const baseChances: Record<SupplyCrateId, number> = {
    free: 0.016,
    mini: 0.032,
    neon: 0.055,
    jackpot: 0.095
  };
  const chance = jackpot ? Math.min(0.24, baseChances[crateId] * 2.1) : baseChances[crateId];
  if (!rollChance(context, chance)) {
    return null;
  }

  const definition = chooseCosmetic(context, save);
  const duplicate = cosmeticUnlocked(save, definition);
  return {
    kind: definition.kind,
    id: definition.id,
    label: definition.label,
    duplicate,
    cashBonus: Math.ceil(baseValue * cosmeticCashShare(crateId) * (jackpot && crateId !== "free" ? 1.45 : 1))
  };
}

function chooseCosmetic(context: RollContext, save: OrbitForgeSave): CrateCosmeticDefinition {
  const locked = crateCosmetics.filter((cosmetic) => !cosmeticUnlocked(save, cosmetic));
  const pool = locked.length > 0 ? locked : crateCosmetics;
  return pool[rollInteger(context, 0, pool.length - 1)];
}

function cosmeticUnlocked(save: OrbitForgeSave, cosmetic: CrateCosmeticDefinition): boolean {
  return cosmetic.kind === "shellSkin"
    ? save.cosmetics.UnlockedCrateShellSkins.includes(cosmetic.id)
    : save.cosmetics.UnlockedCrateCorePatterns.includes(cosmetic.id);
}

function cosmeticCashShare(crateId: SupplyCrateId): number {
  if (crateId === "free") return 0.1;
  if (crateId === "mini") return 0.09;
  if (crateId === "neon") return 0.13;
  return 0.18;
}

function ticketStorageFull(save: OrbitForgeSave): boolean {
  const stats = save.stats;
  return stats.AutoLaunchTickets60 + stats.AutoLaunchTickets120 + stats.AutoLaunchTickets300 + stats.AutoLaunchTickets600 >= 5;
}

function rollChance(context: RollContext, chance: number): boolean {
  return rollUnit(context) < chance;
}

function rollRange(context: RollContext, range: readonly [number, number]): number {
  return range[0] + (range[1] - range[0]) * rollUnit(context);
}

function rollInteger(context: RollContext, min: number, max: number): number {
  return min + Math.floor(rollUnit(context) * (max - min + 1));
}

function rollUnit(context: RollContext): number {
  const next = rngUnit(context.state);
  context.state = next.state;
  return next.value;
}
